import { useEffect, useRef, useCallback } from 'react'
import { useRouter, useLocation } from '@tanstack/react-router'

/**
 * Хук для восстановления позиции скролла в контейнерах с overflow
 * 
 * Работает так:
 * - При навигации вперёд — сбрасывает скролл наверх
 * - При навигации назад (history.back) — восстанавливает позицию
 * 
 * @example
 * ```tsx
 * function MyPage() {
 *   const scrollRef = useScrollRestoration('my-page')
 *   
 *   return (
 *     <div ref={scrollRef} className="overflow-y-auto">
 *       {content}
 *     </div>
 *   )
 * }
 * ```
 */
export function useScrollRestoration(key: string) {
  const containerRef = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const location = useLocation()
  
  // Ключ для sessionStorage
  const storageKey = `scroll-${key}-${location.pathname}`
  
  // Сохраняем позицию скролла перед уходом
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    
    // Сохраняем текущую позицию при уходе со страницы
    const saveScrollPosition = () => {
      const scrollTop = container.scrollTop
      if (scrollTop > 0) {
        sessionStorage.setItem(storageKey, String(scrollTop))
      }
    }
    
    // Слушаем события перед переходом
    const unsubscribe = router.subscribe('onBeforeNavigate', () => {
      saveScrollPosition()
    })
    
    // Также сохраняем при выгрузке страницы
    window.addEventListener('beforeunload', saveScrollPosition)
    
    return () => {
      unsubscribe()
      window.removeEventListener('beforeunload', saveScrollPosition)
    }
  }, [router, storageKey])
  
  // Восстанавливаем позицию при возврате назад
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    
    // Проверяем, это навигация назад или вперёд
    const navigationType = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type
    const isBackNavigation = navigationType === 'back_forward' || 
      window.history.state?.key !== undefined // TanStack Router маркер
    
    // Пытаемся восстановить позицию
    const savedPosition = sessionStorage.getItem(storageKey)
    
    if (savedPosition && isBackNavigation) {
      // Небольшая задержка для рендера контента
      requestAnimationFrame(() => {
        container.scrollTop = parseInt(savedPosition, 10)
      })
    } else {
      // Сбрасываем наверх при навигации вперёд
      container.scrollTop = 0
      // Очищаем сохранённую позицию
      sessionStorage.removeItem(storageKey)
    }
  }, [location.pathname, storageKey])
  
  return containerRef
}

/**
 * Простой хук для сброса скролла наверх при смене страницы
 * Используется когда не нужно восстановление
 */
export function useScrollToTop() {
  const location = useLocation()
  
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])
}

/**
 * Хук для сброса скролла контейнера наверх
 */
export function useScrollContainerToTop(
  containerRef: React.RefObject<HTMLElement>,
  deps: unknown[] = []
) {
  const location = useLocation()
  
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, ...deps])
}

